import { Link } from "react-router-dom";
import type { Article } from "@/types/news";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "wide" | "compact";
}

function formatViews(views: number) {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return `${views}`;
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-3 border-b border-background-200 py-3 last:border-b-0"
      >
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded bg-secondary-100 font-label text-xs font-semibold text-secondary-800">
          {article.channelName.slice(0, 1)}
        </span>
        <span className="min-w-0">
          <span className="block truncate font-body text-sm text-foreground-800 transition-colors group-hover:text-primary-600">
            {article.title}
          </span>
          <span className="mt-1 block font-label text-xs text-secondary-500">
            {article.publishedAt.slice(5)} · 阅读 {formatViews(article.views)}
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group flex flex-col gap-4 sm:flex-row">
        <Link
          to={`/article/${article.id}`}
          className="relative block h-44 w-full shrink-0 overflow-hidden rounded-lg sm:h-32 sm:w-52"
        >
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 爆料官网`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-primary-100 px-2 py-0.5 font-label text-[11px] text-primary-700">
              {article.channelName}
            </span>
            {article.hot && (
              <span className="flex items-center gap-1 font-label text-[11px] text-primary-600">
                <i className="ri-fire-fill" aria-hidden="true"></i>
                热
              </span>
            )}
          </div>
          <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950">
            <Link to={`/article/${article.id}`} className="transition-colors hover:text-primary-600">
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 font-body text-sm leading-6 text-foreground-600">
            {article.summary}
          </p>
          <p className="mt-3 flex items-center gap-3 font-label text-xs text-secondary-500">
            <span>{article.author}</span>
            <span className="h-3 w-px bg-background-300"></span>
            <span>{article.publishedAt}</span>
            <span className="h-3 w-px bg-background-300"></span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {formatViews(article.views)}
            </span>
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.id}`} className="relative block h-48 w-full overflow-hidden">
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          title={`${article.title} - 爆料官网`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded bg-foreground-950/80 px-2 py-0.5 font-label text-[11px] text-background-50">
          {article.channelName}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950">
          <Link to={`/article/${article.id}`} className="transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 font-body text-sm leading-6 text-foreground-600">
          {article.summary}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
          <span className="font-label text-xs text-secondary-500">{article.publishedAt.slice(5)}</span>
          <span className="flex items-center gap-2 font-label text-xs text-secondary-500">
            <i className="ri-eye-line" aria-hidden="true"></i>
            {formatViews(article.views)}
            <span className="h-3 w-px bg-background-300"></span>
            {article.readTime}
          </span>
        </div>
      </div>
    </article>
  );
}